import klaxMascotVideoLight from 'assets/images/magnific_mascott-t-light.mp4';
import klaxMascotVideoDark from 'assets/images/magnific_mascott-t.mp4';
import { useTheme } from 'components/core/theme-provider';
import i18n from 'i18n/i18n';
import { cn } from 'lib/utils';
import { BUDDY_CONFIG } from 'config/buddy/config';
import Buddy from 'features/buddy/Buddy';

const loadingMessagesByLanguage = {
  en: [
    'Warming up the magic',
    'Teaching pixels to behave',
    'Assembling clever thoughts',
    'Polishing your next screen',
    'Brewing something useful',
    'Convincing the app to sparkle',
  ],
  de: [
    'Wir bringen die Magie in Schwung',
    'Wir bringen die Pixel auf Kurs',
    'Wir setzen clevere Gedanken zusammen',
    'Wir polieren deinen nächsten Screen',
    'Wir brauen etwas Nützliches zusammen',
    'Wir überreden die App zu funkeln',
  ],
  it: [
    'Sto scaldando la magia',
    'Insegno ai pixel a comportarsi',
    'Sto mettendo insieme pensieri furbi',
    'Sto lucidando il tuo prossimo schermo',
    'Sto preparando qualcosa di utile',
    'Sto convincendo l’app a brillare',
  ],
  fr: [
    'On réchauffe la magie',
    'On apprend aux pixels à se tenir',
    'On assemble des idées malines',
    'On peaufine votre prochain écran',
    'On concocte quelque chose d’utile',
    'On convainc l’appli de pétiller',
  ],
} as const;

// Picked once per page load. Several loaders mount one after another during startup,
// and a new line on each would make the text jump.
const messageIndex = Math.floor(Math.random() * loadingMessagesByLanguage.en.length);
// Overlay runs before UILM is loaded, so t() stays off until that is solved.
// import { useTranslation } from 'react-i18next';
// const LOADING_MESSAGES = [
//   { key: 'LOADING_MESSAGE_A', defaultValue: 'Warming up the magic' },
//   { key: 'LOADING_MESSAGE_B', defaultValue: 'Teaching pixels to behave' },
//   { key: 'LOADING_MESSAGE_C', defaultValue: 'Assembling clever thoughts' },
//   { key: 'LOADING_MESSAGE_D', defaultValue: 'Polishing your next screen' },
//   { key: 'LOADING_MESSAGE_E', defaultValue: 'Brewing something useful' },
//   { key: 'LOADING_MESSAGE_F', defaultValue: 'Convincing the app to sparkle' },
// ] as const;
// const { t } = useTranslation();
// const { key, defaultValue } = LOADING_MESSAGES[messageIndex % LOADING_MESSAGES.length];
// const loadingMessage = t(key, { defaultValue });

export function LoadingOverlay() {
  const { resolvedTheme } = useTheme();
  const storedLanguage = localStorage.getItem('language');
  const storedTheme = localStorage.getItem('theme');
  const documentTheme = document.documentElement.classList.contains('dark') ? 'dark' : 'light';
  const systemTheme = window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
  const currentLanguage = storedLanguage || i18n.language || 'en-US';
  const languageCode = currentLanguage.split('-')[0].toLowerCase() as keyof typeof loadingMessagesByLanguage;
  const loadingMessages = loadingMessagesByLanguage[languageCode] ?? loadingMessagesByLanguage.en;
  const loadingMessage = loadingMessages[messageIndex % loadingMessages.length];
  const effectiveTheme = storedTheme === 'dark'
    ? 'dark'
    : storedTheme === 'light'
      ? 'light'
      : documentTheme === 'dark'
        ? 'dark'
        : resolvedTheme === 'dark'
          ? 'dark'
          : systemTheme;
  const isDark = effectiveTheme === 'dark';
  const klaxMascotVideo = isDark ? klaxMascotVideoDark : klaxMascotVideoLight;
  // Inline color: this overlay mounts before ThemeProvider adds `.dark`, so `bg-white` stays cream and mix-blend-screen hides the mascot.
  const backgroundColor = isDark ? 'hsl(240 5% 9%)' : 'hsla(0, 0%, 100%, 0.92)';

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center backdrop-blur-sm"
      style={{ backgroundColor }}
      data-testid="loading-overlay"
    >
      <div className="flex min-w-0 flex-col items-center gap-3 px-4">
        {/* Buddy is off in staging and production, so the mascot video stays as the fallback. */}
        {BUDDY_CONFIG.on ? (
          <Buddy inline />
        ) : (
          <div className="size-12 overflow-hidden rounded-full">
            <video
              key={effectiveTheme}
              src={klaxMascotVideo}
              autoPlay
              loop
              muted
              playsInline
              aria-hidden
              className={cn(
                'size-full object-cover',
                isDark ? 'scale-[1.30] mix-blend-screen' : 'scale-[1.30]'
              )}
              width={48}
              height={48}
            />
          </div>
        )}

        <p
          className={cn(
            'min-w-0 max-w-full break-words text-center font-heyJune text-xl font-normal leading-none sm:text-3xl 2xl:text-4xl',
            isDark ? 'text-greeting-muted' : 'text-foreground'
          )}
        >
          {loadingMessage}
        </p>
      </div>
    </div>
  );
}
